import type { HeroPose } from './cast';

/**
 * Tricks that buff the caster. The rival does not flinch.
 * A sabotage still hits, even if it were ever listed here.
 */
export const QUIET_POWERUPS: ReadonlySet<string> = new Set([
  'snail-mail',
  'thicket-shield',
  'hearthstone',
  'seedling',
  'fox-mask',
  'moonlit-mark',
]);

export interface PoseReaction {
  /** A 5-match or a cascade of 3 or more happened in this resolution. */
  bigCombo: boolean;
  /** Who is flinching from a hostile trick this resolution. */
  victim: 0 | 1 | null;
  /** Stays true after a boast so a later small score does not drop back to cheer. */
  boasted: [boolean, boolean];
}

export function freshReaction(): PoseReaction {
  return { bigCombo: false, victim: null, boasted: [false, false] };
}

export function noteMatchSize(reaction: PoseReaction, size: number): void {
  if (size >= 5) reaction.bigCombo = true;
}

/** CascadeEvent.index is the 1-based extra wave. Three means a cascade of 3. */
export function noteCascadeIndex(reaction: PoseReaction, index: number): void {
  if (index >= 3) reaction.bigCombo = true;
}

function other(player: 0 | 1): 0 | 1 {
  return player === 0 ? 1 : 0;
}

function same(a: [HeroPose, HeroPose], b: [HeroPose, HeroPose]): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

/**
 * Cheer on a scoring move. Boast when the move is a big combo.
 * The rival slumps only for that big combo, and a hurt rival stays hurt.
 * Returns null when the poses already match, so the tween is not restarted.
 */
export function moodsAfterScore(
  reaction: PoseReaction,
  player: 0 | 1,
  cascade: number,
  current: [HeroPose, HeroPose],
): [HeroPose, HeroPose] | null {
  const big = reaction.bigCombo || cascade >= 3;
  if (big) reaction.boasted[player] = true;
  const pose: HeroPose = reaction.boasted[player] ? 'boast' : 'cheer';
  const next: [HeroPose, HeroPose] = [current[0], current[1]];
  next[player] = pose;
  if (reaction.victim !== null) next[reaction.victim] = 'hurt';
  else if (big) {
    const rival = other(player);
    if (next[rival] === 'idle' || next[rival] === 'dismay') next[rival] = 'dismay';
  }
  return same(next, current) ? null : next;
}

/** Signature power. Ordinary tricks do not use this pose. */
export function moodsAfterPower(
  player: 0 | 1,
  victim: 0 | 1 | null,
): [HeroPose, HeroPose] {
  const next: [HeroPose, HeroPose] = ['idle', 'idle'];
  next[player] = 'power';
  if (victim !== null && victim !== player) next[victim] = 'hurt';
  return next;
}

export function powerupHitsRival(
  id: string,
  blocked: boolean | undefined,
  sabotage: boolean | undefined,
): boolean {
  if (blocked) return false;
  if (sabotage) return true;
  return !QUIET_POWERUPS.has(id);
}

/** The rival flinches. Returns null for a self-buff or a shielded trick. */
export function moodsAfterPowerup(
  reaction: PoseReaction,
  player: 0 | 1,
  id: string,
  blocked: boolean | undefined,
  sabotage: boolean | undefined,
  current: [HeroPose, HeroPose],
): [HeroPose, HeroPose] | null {
  if (!powerupHitsRival(id, blocked, sabotage)) return null;
  const rival = other(player);
  reaction.victim = rival;
  const next: [HeroPose, HeroPose] = [current[0], current[1]];
  next[rival] = 'hurt';
  return same(next, current) ? null : next;
}
